import React from 'react';
import { NavBar } from '../components/NavBar';
import { Logo } from '../components/Logo';
import { ProductCard } from './ProductCard';
import { PRODUCTS, OFFER_CARDS, OFFER_TEXTS, productShortName } from './products';
import { useIsMobile } from '../hooks/useIsMobile';
import { colors, spacing, typography, radius, brandGradient, cardStyle, pageStyle, } from '../theme';
// ---------------------------------------------------------------------------
// Hero Section
// ---------------------------------------------------------------------------
const Hero = () => {
    const isMobile = useIsMobile();
    return (React.createElement("section", { style: {
            background: `linear-gradient(160deg, ${colors.dark} 0%, #1E293B 100%)`,
            padding: `${spacing.xxl}px ${isMobile ? spacing.md : spacing.xl}px`,
            textAlign: 'center',
        } },
        React.createElement("div", { style: { maxWidth: 720, margin: '0 auto' } },
            React.createElement("div", { style: { marginBottom: spacing.md } },
                React.createElement(Logo, { size: typography.fontSizeXl })),
            React.createElement("h1", { style: {
                    margin: `0 0 ${spacing.md}px 0`,
                    fontSize: isMobile ? 28 : typography.fontSizeHero,
                    fontWeight: 800,
                    fontFamily: typography.fontFamily,
                    color: colors.white,
                    lineHeight: 1.15,
                    letterSpacing: '-0.02em',
                } }, "Ship Faster with Governed DevOps"),
            React.createElement("p", { style: {
                    margin: `0 0 ${spacing.md}px 0`,
                    fontSize: typography.fontSizeMd,
                    color: '#94A3B8',
                    fontFamily: typography.fontFamily,
                    lineHeight: 1.7,
                    maxWidth: 620,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                } }, "Five Azure DevOps extensions that enforce governance without slowing your team down. Freeze work items after approval, control state transitions, validate iteration consistency, filter backlogs intelligently, and query across the full SAFe hierarchy."),
            React.createElement("p", { style: {
                    margin: `0 0 ${spacing.xl}px 0`,
                    fontSize: typography.fontSizeSm,
                    color: '#64748B',
                    fontFamily: typography.fontFamily,
                    lineHeight: 1.6,
                    maxWidth: 480,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                } },
                OFFER_TEXTS.hero,
                " Available in 14 languages."),
            React.createElement("div", { style: { display: 'flex', gap: spacing.md, justifyContent: 'center', flexWrap: 'wrap' } },
                React.createElement("a", { href: "#products", style: {
                        display: 'inline-block',
                        padding: `${spacing.sm + 2}px ${spacing.xl}px`,
                        background: brandGradient,
                        color: colors.white,
                        textDecoration: 'none',
                        borderRadius: radius.button,
                        fontSize: typography.fontSizeBase,
                        fontFamily: typography.fontFamily,
                        fontWeight: 700,
                        boxShadow: `0 4px 16px ${colors.primary}55`,
                    }, "data-testid": "hero-cta" }, OFFER_TEXTS.heroCta),
                React.createElement("a", { href: "/customer", style: {
                        display: 'inline-block',
                        padding: `${spacing.sm + 2}px ${spacing.xl}px`,
                        border: `1.5px solid ${colors.accent}`,
                        color: colors.accent,
                        textDecoration: 'none',
                        borderRadius: radius.button,
                        fontSize: typography.fontSizeBase,
                        fontFamily: typography.fontFamily,
                        fontWeight: 600,
                    } }, "Customer Portal")))));
};
// ---------------------------------------------------------------------------
// Trust bar
// ---------------------------------------------------------------------------
const TrustBar = () => (React.createElement("div", { style: {
        backgroundColor: colors.white,
        borderBottom: `1px solid ${colors.border}`,
        padding: `${spacing.md}px ${spacing.xl}px`,
        display: 'flex',
        gap: spacing.xl,
        justifyContent: 'center',
        flexWrap: 'wrap',
    } }, ['Built for Azure DevOps & SAFe', OFFER_TEXTS.trustBar, 'No credit card required', '14 languages supported'].map((item) => (React.createElement("span", { key: item, style: {
        fontSize: typography.fontSizeSm,
        fontFamily: typography.fontFamily,
        color: colors.textSecondary,
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        gap: spacing.xs,
    } },
    React.createElement("span", { style: { color: colors.success, fontWeight: 700 } }, "\u2713"),
    item)))));
// ---------------------------------------------------------------------------
// Products section
// ---------------------------------------------------------------------------
const ProductsSection = () => {
    const isMobile = useIsMobile();
    return (React.createElement("section", { id: "products", style: {
            padding: `${spacing.xxl}px ${isMobile ? spacing.md : spacing.xl}px`,
            backgroundColor: colors.lightBg,
        } },
        React.createElement("div", { style: { maxWidth: 1200, margin: '0 auto' } },
            React.createElement("div", { style: { textAlign: 'center', marginBottom: spacing.xl } },
                React.createElement("h2", { style: {
                        margin: `0 0 ${spacing.sm}px 0`,
                        fontSize: typography.fontSizeXl,
                        fontWeight: 800,
                        fontFamily: typography.fontFamily,
                        color: colors.textPrimary,
                    } }, "Governance Tools That Teams Actually Use"),
                React.createElement("p", { style: {
                        margin: 0,
                        fontSize: typography.fontSizeBase,
                        color: colors.textSecondary,
                        fontFamily: typography.fontFamily,
                        maxWidth: 640,
                        marginLeft: 'auto',
                        marginRight: 'auto',
                    } }, "Each extension solves one governance gap in Azure DevOps \u2014 from freezing approved work items to enforcing state transitions and validating iteration consistency across your SAFe hierarchy.")),
            React.createElement("div", { style: {
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: spacing.lg,
                    justifyContent: 'center',
                } }, PRODUCTS.map((product) => (React.createElement(ProductCard, { key: product.id, product: product })))))));
};
function fmtEur(cents) {
    return `€${(cents / 100).toFixed(2)}`;
}
const PricingSection = () => {
    const isMobile = useIsMobile();
    const [billing, setBilling] = React.useState('monthly');
    const [apiData, setApiData] = React.useState(null);
    // A failed request is NOT the same state as a pending one. Without this flag the
    // card shows "Loading pricing..." forever, promising a value that will never arrive.
    const [pricingFailed, setPricingFailed] = React.useState(false);
    React.useEffect(() => {
        fetch('/api/pricing')
            .then((r) => r.json())
            .then(setApiData)
            // There is no static fallback any more (WI#4536) -- swallowing the error here would
            // leave the card pending forever instead of saying that the price is unavailable.
            .catch(() => setPricingFailed(true));
    }, []);
    // Prices per OFFER card (WI#4566 B1b): the cheapest amount of the card's tier across the
    // products that carry this offer -- P1 per-user 2.49 against P4/P5 2.99 gives 'from 2.49',
    // P2/P3 project 349. Read live from /api/pricing; there is no static fallback (WI#4536).
    // Before B1b the only card with a price was 'Pro', fed from P1 per-user alone (WI#4533);
    // it advertised a per-user licence for P2/P3, which have none.
    const pricePending = apiData || pricingFailed ? 'Pricing temporarily unavailable' : 'Loading pricing\u2026';
    const cheapest = (card, mode) => {
        const amounts = PRODUCTS.filter((p) => p.offer === card.offer)
            .map((p) => apiData?.extensions?.[p.id]?.tiers?.[card.fromTier]?.[mode])
            .filter((amount) => typeof amount === 'number');
        return amounts.length > 0 ? Math.min(...amounts) : undefined;
    };
    const cardData = OFFER_CARDS.map((card) => {
        const monthly = cheapest(card, 'monthly');
        const annual = cheapest(card, 'annual');
        const perMonth = billing === 'monthly' ? monthly : annual != null ? Math.round(annual / 12) : undefined;
        return {
            card,
            products: PRODUCTS.filter((p) => p.offer === card.offer).map(productShortName),
            fromNote: perMonth == null
                ? pricePending
                : `${card.fromLabel} ${fmtEur(perMonth)} / ${card.fromUnit} / month`,
            billedNote: billing === 'annual' && annual != null
                ? `Billed ${fmtEur(annual)} / ${card.fromUnit} / year`
                : null,
        };
    });
    // The annual saving, measured from the live prices instead of a fixed claim: the toggle said
    // 'Save 20%' while every annual price is 10.8 x the monthly one, i.e. 10 % (WI#4566 B1b).
    // No prices, no number -- a badge without a source is exactly what it replaced.
    const savingPct = (() => {
        for (const ext of Object.values(apiData?.extensions ?? {})) {
            for (const tier of Object.values(ext.tiers ?? {})) {
                if (tier.monthly && tier.annual) {
                    return Math.round((1 - tier.annual / (12 * tier.monthly)) * 100);
                }
            }
        }
        return null;
    })();
    return (React.createElement("section", { id: "pricing", style: {
            padding: `${spacing.xxl}px ${isMobile ? spacing.md : spacing.xl}px`,
            backgroundColor: colors.white,
        } },
        React.createElement("div", { style: { maxWidth: 800, margin: '0 auto' } },
            React.createElement("div", { style: { textAlign: 'center', marginBottom: spacing.xl } },
                React.createElement("h2", { style: {
                        margin: `0 0 ${spacing.sm}px 0`,
                        fontSize: typography.fontSizeXl,
                        fontWeight: 800,
                        fontFamily: typography.fontFamily,
                        color: colors.textPrimary,
                    } }, "Simple, transparent pricing"),
                React.createElement("p", { style: {
                        margin: `0 0 ${spacing.lg}px 0`,
                        fontSize: typography.fontSizeBase,
                        color: colors.textSecondary,
                        fontFamily: typography.fontFamily,
                    } }, "Prices per month, excl. VAT. Cancel any time."),
                React.createElement("div", { style: {
                        display: 'inline-flex',
                        borderRadius: 24,
                        border: `1.5px solid ${colors.border}`,
                        overflow: 'hidden',
                        fontFamily: typography.fontFamily,
                    } }, ['monthly', 'annual'].map((mode) => (React.createElement("button", { key: mode, onClick: () => setBilling(mode), style: {
                        padding: `${spacing.xs + 2}px ${spacing.lg}px`,
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: typography.fontSizeSm,
                        fontWeight: billing === mode ? 700 : 400,
                        fontFamily: typography.fontFamily,
                        background: billing === mode ? colors.primary : 'transparent',
                        color: billing === mode ? colors.white : colors.textSecondary,
                        transition: 'background 0.15s, color 0.15s',
                    } },
                    mode === 'monthly' ? 'Monthly' : 'Annual',
                    mode === 'annual' && savingPct != null && (React.createElement("span", { "data-testid": "pricing-annual-saving", style: {
                            marginLeft: spacing.xs,
                            background: colors.success,
                            color: colors.white,
                            fontSize: typography.fontSizeXs,
                            fontWeight: 700,
                            padding: `1px ${spacing.xs}px`,
                            borderRadius: 10,
                        } },
                        "Save ",
                        savingPct,
                        "%"))))))),
            React.createElement("div", { style: {
                    display: 'flex',
                    gap: spacing.lg,
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                } }, cardData.map(({ card, products, fromNote, billedNote }) => (React.createElement("div", { key: card.offer, "data-testid": `offer-card-${card.offer}`, style: {
                    ...cardStyle,
                    flex: '1 1 280px',
                    maxWidth: 340,
                    border: `1px solid ${colors.border}`,
                    display: 'flex',
                    flexDirection: 'column',
                } },
                React.createElement("div", { style: {
                        fontSize: typography.fontSizeLg,
                        fontWeight: 800,
                        fontFamily: typography.fontFamily,
                        color: colors.textPrimary,
                        marginBottom: spacing.xs,
                    } }, card.title),
                React.createElement("div", { "data-testid": `offer-products-${card.offer}`, style: {
                        fontSize: typography.fontSizeXs,
                        fontFamily: typography.fontFamily,
                        color: colors.textSecondary,
                        marginBottom: spacing.md,
                    } }, products.join(' \u00b7 ')),
                React.createElement("div", { "data-testid": `offer-headline-${card.offer}`, style: {
                        fontSize: typography.fontSizeBase,
                        fontFamily: typography.fontFamily,
                        color: colors.primary,
                        fontWeight: 700,
                        marginBottom: spacing.xs,
                    } }, card.headline),
                React.createElement("div", { "data-testid": `offer-price-${card.offer}`, style: {
                        fontSize: typography.fontSizeSm,
                        fontFamily: typography.fontFamily,
                        color: colors.textPrimary,
                        fontWeight: 600,
                        marginBottom: billedNote ? spacing.xs : spacing.lg,
                    } }, fromNote),
                billedNote && (React.createElement("div", { style: {
                        fontSize: typography.fontSizeXs,
                        fontFamily: typography.fontFamily,
                        color: colors.textSecondary,
                        marginBottom: spacing.lg,
                    } }, billedNote)),
                React.createElement("ul", { style: {
                        margin: `0 0 ${spacing.lg}px 0`,
                        padding: 0,
                        listStyle: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: spacing.sm,
                        flexGrow: 1,
                    } }, card.features.map((f) => (React.createElement("li", { key: f, style: {
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: spacing.sm,
                        fontSize: typography.fontSizeSm,
                        fontFamily: typography.fontFamily,
                        color: colors.textSecondary,
                        lineHeight: 1.5,
                    } },
                    React.createElement("span", { style: {
                            color: colors.success,
                            fontWeight: 700,
                            flexShrink: 0,
                            lineHeight: 1.5,
                        } }, '\u2713'),
                    f)))),
                React.createElement("a", { "data-testid": `offer-cta-primary-${card.offer}`, href: card.primaryCta.href, style: {
                        display: 'block',
                        textAlign: 'center',
                        padding: `${spacing.sm + 2}px ${spacing.md}px`,
                        background: brandGradient,
                        color: colors.white,
                        textDecoration: 'none',
                        borderRadius: radius.button,
                        fontSize: typography.fontSizeBase,
                        fontFamily: typography.fontFamily,
                        fontWeight: 700,
                        boxShadow: `0 4px 14px ${colors.primary}44`,
                        marginBottom: spacing.sm,
                    } }, card.primaryCta.label),
                React.createElement("a", { "data-testid": `offer-cta-secondary-${card.offer}`, href: card.secondaryCta.href, style: {
                        display: 'block',
                        textAlign: 'center',
                        padding: `${spacing.sm + 2}px ${spacing.md}px`,
                        background: 'transparent',
                        border: `1.5px solid ${colors.primary}`,
                        color: colors.primary,
                        textDecoration: 'none',
                        borderRadius: radius.button,
                        fontSize: typography.fontSizeBase,
                        fontFamily: typography.fontFamily,
                        fontWeight: 700,
                    } }, card.secondaryCta.label))))))));
};
// ---------------------------------------------------------------------------
// Contact Section (WI #695, #719)
// Backend endpoint POST /api/contact lives in api/src/index.ts (Bug B L7-Migration
// 2026-05-05: BYO Function App `cool-tools-api`, gelinkt an SWA Standard via
// linkedBackends). Validates input, sends notification email via Resend, returns 200.
// ---------------------------------------------------------------------------
const ContactSection = () => {
    const [name, setName] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [message, setMessage] = React.useState('');
    const [status, setStatus] = React.useState('idle');
    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('sending');
        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, email, message }),
            });
            setStatus(res.ok ? 'sent' : 'error');
        }
        catch {
            setStatus('error');
        }
    };
    const inputStyle = {
        width: '100%',
        padding: `${spacing.sm}px ${spacing.md}px`,
        border: `1px solid ${colors.border}`,
        borderRadius: radius.button,
        fontSize: typography.fontSizeBase,
        fontFamily: typography.fontFamily,
        color: colors.textPrimary,
        backgroundColor: colors.white,
        boxSizing: 'border-box',
    };
    return (React.createElement("section", { id: "contact", style: {
            backgroundColor: colors.lightBg,
            padding: `${spacing.xxl}px ${spacing.xl}px`,
        } },
        React.createElement("div", { style: { maxWidth: 560, margin: '0 auto' } },
            React.createElement("h2", { style: {
                    margin: `0 0 ${spacing.sm}px 0`,
                    fontSize: typography.fontSizeXl,
                    fontWeight: 800,
                    fontFamily: typography.fontFamily,
                    color: colors.textPrimary,
                    textAlign: 'center',
                } }, "Get in touch"),
            React.createElement("p", { style: {
                    margin: `0 0 ${spacing.xl}px 0`,
                    fontSize: typography.fontSizeMd,
                    color: colors.textSecondary,
                    fontFamily: typography.fontFamily,
                    textAlign: 'center',
                } }, "Questions, enterprise pricing or partnership inquiries \u2014 we respond within one business day."),
            status === 'sent' ? (React.createElement("div", { style: {
                    padding: spacing.lg,
                    backgroundColor: colors.successBg,
                    borderRadius: radius.card,
                    color: colors.success,
                    fontFamily: typography.fontFamily,
                    textAlign: 'center',
                    fontWeight: 600,
                } }, "Message received \u2014 we'll be in touch shortly.")) : (React.createElement("form", { onSubmit: handleSubmit, style: { display: 'flex', flexDirection: 'column', gap: spacing.md } },
                React.createElement("input", { type: "text", placeholder: "Your name", required: true, value: name, onChange: (e) => setName(e.target.value), style: inputStyle }),
                React.createElement("input", { type: "email", placeholder: "Work email", required: true, value: email, onChange: (e) => setEmail(e.target.value), style: inputStyle }),
                React.createElement("textarea", { placeholder: "How can we help?", required: true, rows: 4, value: message, onChange: (e) => setMessage(e.target.value), style: { ...inputStyle, resize: 'vertical' } }),
                status === 'error' && (React.createElement("p", { style: { margin: 0, color: colors.error, fontSize: typography.fontSizeSm, fontFamily: typography.fontFamily } }, "Something went wrong. Please try again or email us at gerd.marinelli@cool-tools.app")),
                React.createElement("button", { type: "submit", disabled: status === 'sending', style: {
                        padding: `${spacing.sm + 2}px ${spacing.xl}px`,
                        background: brandGradient,
                        color: colors.white,
                        border: 'none',
                        borderRadius: radius.button,
                        fontSize: typography.fontSizeBase,
                        fontFamily: typography.fontFamily,
                        fontWeight: 700,
                        cursor: status === 'sending' ? 'not-allowed' : 'pointer',
                        opacity: status === 'sending' ? 0.7 : 1,
                    } }, status === 'sending' ? 'Sending…' : 'Send message'))))));
};
// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------
const Footer = () => (React.createElement("footer", { style: {
        backgroundColor: colors.dark,
        padding: `${spacing.xl}px ${spacing.xl}px`,
        color: colors.textMuted,
        fontFamily: typography.fontFamily,
    } },
    React.createElement("div", { style: {
            maxWidth: 1200,
            margin: '0 auto',
            display: 'flex',
            flexWrap: 'wrap',
            gap: spacing.xl,
            justifyContent: 'space-between',
            alignItems: 'flex-start',
        } },
        React.createElement("div", null,
            React.createElement(Logo, { size: typography.fontSizeBase }),
            React.createElement("p", { style: {
                    margin: `${spacing.sm}px 0 0 0`,
                    fontSize: typography.fontSizeXs,
                    color: colors.textMuted,
                    maxWidth: 280,
                    lineHeight: 1.6,
                } }, "Productivity Tools for Professionals. Built for teams who demand quality.")),
        React.createElement("div", { style: { display: 'flex', gap: spacing.xxl, flexWrap: 'wrap' } },
            React.createElement("div", null,
                React.createElement("div", { style: {
                        fontSize: typography.fontSizeXs,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: colors.white,
                        marginBottom: spacing.sm,
                    } }, "Product"),
                ['P1 ADO Filter', 'P2 Work Item Freeze', 'P3 Status Freeze', 'P4 Iteration Checker', 'P5 Multi-Level Query'].map((name) => (React.createElement("div", { key: name, style: { marginBottom: spacing.xs } },
                    React.createElement("a", { href: "#products", style: {
                            color: colors.textMuted,
                            textDecoration: 'none',
                            fontSize: typography.fontSizeSm,
                        } }, name))))),
            React.createElement("div", null,
                React.createElement("div", { style: {
                        fontSize: typography.fontSizeXs,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: colors.white,
                        marginBottom: spacing.sm,
                    } }, "Company"),
                [
                    { label: 'Customer Portal', href: '/customer' },
                    { label: 'Support', href: '/support' },
                    { label: 'Privacy Policy', href: '/privacy' },
                    { label: 'Terms of Use', href: '/terms' },
                    { label: 'Imprint', href: '/imprint' },
                ].map(({ label, href }) => (React.createElement("div", { key: label, style: { marginBottom: spacing.xs } },
                    React.createElement("a", { href: href, style: {
                            color: colors.textMuted,
                            textDecoration: 'none',
                            fontSize: typography.fontSizeSm,
                        } }, label))))))),
    React.createElement("div", { style: {
            maxWidth: 1200,
            margin: `${spacing.xl}px auto 0`,
            paddingTop: spacing.lg,
            borderTop: `1px solid #1E293B`,
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: spacing.sm,
            fontSize: typography.fontSizeXs,
            color: colors.textMuted,
        } },
        React.createElement("span", null,
            "\u00A9 ",
            new Date().getFullYear(),
            " cool-tools. All rights reserved."),
        React.createElement("span", null,
            React.createElement("a", { href: "mailto:gerd.marinelli@cool-tools.app", style: { color: colors.textMuted, textDecoration: 'none' } }, "gerd.marinelli@cool-tools.app")))));
// ---------------------------------------------------------------------------
// FAQ Section
// ---------------------------------------------------------------------------
const FAQ_ITEMS = [
    {
        q: OFFER_TEXTS.faqQuestion,
        a: OFFER_TEXTS.faqAnswer,
    },
    {
        q: 'How is billing handled?',
        a: 'We use Stripe for secure payment processing. You can pay monthly or annually (10% discount). All invoices and subscription management are available in the Customer Portal. We do not use VS Marketplace Paid billing.',
    },
    {
        q: 'Can I install extensions in multiple organizations?',
        // WI#4566 B1b (Gerd 2026-09-30: "so dass es der aktuellen Implementierung entspricht"):
        // 'Each license covers one ADO organization' was false -- a licence is activated per person
        // in the extension and is valid in every organisation (api/src/license-lookup.ts, priority 1),
        // and team members get theirs by invitation code. Multi-organisation use stays (decision
        // matrix in the WorkResult); seat limits per subscription follow with #4567.
        a: 'Yes. A licence belongs to your subscription, not to one organization: each user activates it in the extension, in any of your ADO organizations, and you invite team members from the Customer Portal. Contact us for volume discounts on 3+ organizations.',
    },
    {
        q: 'What languages are supported?',
        a: 'All 14 Azure DevOps UI languages: English, German, French, Spanish, Italian, Japanese, Korean, Portuguese (Brazil), Russian, Chinese (Simplified & Traditional), Czech, Polish, and Turkish. The extension automatically matches your ADO language setting.',
    },
    {
        q: 'Is my data safe?',
        // WI#4619 AC-7: this answer used to say "All extension data stays within your Azure DevOps
        // organization" and "We are GDPR compliant". The first is false by the processing record
        // (Wiki 766): licence check, telemetry, chat and feedback leave the organization. The answer
        // now says what stays and what leaves, in the words of the privacy policy.
        // WI#4617 S5c: telemetry, chat and feedback from an extension are opt-in since WI#4617 -- the
        // answer described telemetry as opt-out until then (guard: legal-pages.test.ts Part 5).
        a: 'The content of your work items stays in your Azure DevOps organization \u2014 the extensions read and change it there with your own permissions, and settings such as freeze rules and query presets are kept in Azure DevOps\u2019 Extension Data Service. Some data does leave your organization: the licence check (identifiers of your user and organization), whatever you send to the AI suggestions, and \u2014 only if you consent when an extension asks \u2014 pseudonymous usage and error telemetry and whatever you send through the support chat or the feedback form. Without consent the extensions stay fully usable. Our Privacy Policy lists each of these with its purpose, recipients and retention.',
    },
    {
        // WI#4536: this answer used to promise a Suite Bundle at \u20ac13.15/user/month. That
        // bundle was never purchasable, and its total included per-user rates for P2 and P3,
        // which are licensed per project or organisation. Promising a price nobody can pay is
        // worse than naming none.
        q: 'Do you offer a SAFe bundle?',
        a: 'Not yet. Each extension is licensed on its own today, so you can combine exactly the ones your teams need \u2014 per user, per project or per organisation, depending on the extension. We are putting bundles together for a later release, in combinations that make sense in licensing terms rather than simply adding everything up.',
    },
];
const FaqSection = () => {
    const [openIndex, setOpenIndex] = React.useState(null);
    return (React.createElement("section", { id: "faq", style: {
            padding: `${spacing.xxl}px ${spacing.xl}px`,
            backgroundColor: colors.lightBg,
        } },
        React.createElement("div", { style: { maxWidth: 720, margin: '0 auto' } },
            React.createElement("h2", { style: {
                    margin: `0 0 ${spacing.xl}px 0`,
                    fontSize: typography.fontSizeXl,
                    fontWeight: 800,
                    fontFamily: typography.fontFamily,
                    color: colors.textPrimary,
                    textAlign: 'center',
                } }, "Frequently Asked Questions"),
            FAQ_ITEMS.map((item, i) => (React.createElement("div", { key: i, style: {
                    borderBottom: `1px solid ${colors.border}`,
                    padding: `${spacing.md}px 0`,
                } },
                React.createElement("button", { onClick: () => setOpenIndex(openIndex === i ? null : i), style: {
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: `${spacing.sm}px 0`,
                        fontFamily: typography.fontFamily,
                        fontSize: typography.fontSizeBase,
                        fontWeight: 600,
                        color: colors.textPrimary,
                        textAlign: 'left',
                    }, "aria-expanded": openIndex === i },
                    item.q,
                    React.createElement("span", { style: { fontSize: 18, color: colors.textMuted, flexShrink: 0, marginLeft: spacing.md } }, openIndex === i ? '\u2212' : '+')),
                openIndex === i && (React.createElement("p", { style: {
                        margin: `${spacing.sm}px 0 0 0`,
                        fontSize: typography.fontSizeSm,
                        color: colors.textSecondary,
                        fontFamily: typography.fontFamily,
                        lineHeight: 1.7,
                    } }, item.a))))))));
};
// ---------------------------------------------------------------------------
// Why Section — Empirical USP
// ---------------------------------------------------------------------------
const PAIN_POINTS = [
    {
        icon: '\ud83d\udce9',
        title: 'Someone edited a closed Sprint item',
        problem: 'Your audit trail is broken. The approved work item was changed after the review. No one noticed until the auditor did.',
        solution: 'Work Item Freeze locks items after approval. Exception requests leave a full audit trail.',
    },
    {
        icon: '\ud83d\udd04',
        title: 'A Story jumped from Active to Done',
        problem: 'Skipping Testing. Your process says Active \u2192 Testing \u2192 Done, but ADO doesn\u2019t enforce it. Work Item Rules have strict limits.',
        solution: 'Status Freeze blocks invalid transitions in real-time. PI-end presets lock changes before a release.',
    },
];
const WhySection = () => (React.createElement("section", { id: "why", style: {
        padding: `${spacing.xxl}px ${spacing.xl}px`,
        backgroundColor: colors.white,
    } },
    React.createElement("div", { style: { maxWidth: 960, margin: '0 auto' } },
        React.createElement("div", { style: { textAlign: 'center', marginBottom: spacing.xl } },
            React.createElement("h2", { style: {
                    margin: `0 0 ${spacing.sm}px 0`,
                    fontSize: typography.fontSizeXl,
                    fontWeight: 800,
                    fontFamily: typography.fontFamily,
                    color: colors.textPrimary,
                } }, "Data-Driven Agility, Not Framework Theater"),
            React.createElement("p", { style: {
                    margin: 0,
                    fontSize: typography.fontSizeBase,
                    color: colors.textSecondary,
                    fontFamily: typography.fontFamily,
                    maxWidth: 640,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    lineHeight: 1.7,
                } },
                "Stop planning with numbers you never hit. Plan with what your teams actually deliver ",
                '\u2014',
                " measured across PIs, not estimated in spreadsheets.")),
        React.createElement("div", { style: { display: 'flex', flexDirection: 'column', gap: spacing.lg } }, PAIN_POINTS.map((point) => (React.createElement("div", { key: point.title, style: {
                ...cardStyle,
                display: 'flex',
                gap: spacing.lg,
                alignItems: 'flex-start',
            } },
            React.createElement("span", { style: { fontSize: 32, flexShrink: 0, lineHeight: 1 } }, point.icon),
            React.createElement("div", null,
                React.createElement("h3", { style: {
                        margin: `0 0 ${spacing.xs}px 0`,
                        fontSize: typography.fontSizeBase,
                        fontWeight: 700,
                        fontFamily: typography.fontFamily,
                        color: colors.textPrimary,
                    } },
                    "\u201C",
                    point.title,
                    "\u201D"),
                React.createElement("p", { style: {
                        margin: `0 0 ${spacing.sm}px 0`,
                        fontSize: typography.fontSizeSm,
                        color: colors.error,
                        fontFamily: typography.fontFamily,
                        lineHeight: 1.6,
                    } }, point.problem),
                React.createElement("p", { style: {
                        margin: 0,
                        fontSize: typography.fontSizeSm,
                        color: colors.success,
                        fontFamily: typography.fontFamily,
                        fontWeight: 600,
                        lineHeight: 1.6,
                    } },
                    '\u2192',
                    " ",
                    point.solution)))))))));
// ---------------------------------------------------------------------------
// App root
// ---------------------------------------------------------------------------
export const App = () => (React.createElement("div", { style: pageStyle },
    React.createElement(NavBar, { links: [
            { label: 'Extensions', href: '#products' },
            { label: 'Pricing', href: '#pricing' },
            { label: 'FAQ', href: '#faq' },
            { label: 'Contact', href: '#contact' },
            { label: 'Customer Portal', href: '/customer' },
        ] }),
    React.createElement(Hero, null),
    React.createElement(TrustBar, null),
    React.createElement(WhySection, null),
    React.createElement(ProductsSection, null),
    React.createElement(PricingSection, null),
    React.createElement(FaqSection, null),
    React.createElement(ContactSection, null),
    React.createElement(Footer, null)));
